import { useEffect, useRef, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import YourNotesRow from '../YourNotesRow';
import YourNotesNodeHeader from './YourNotesNodeHeader';
import YourNotesNodePager from './YourNotesNodePager';
import TopicGroupSearchDialog from '../TopicGroupSearchDialog/TopicGroupSearchDialog';
import topicGroupEngine from '../YourNotesGroupManager/TopicGroupEngine';
import useYourNotesNode from './useYourNotesNode';
import useKeyboardNav from '../../../../hooks/useKeyboardNav';
import useCrossNodeNav from '../../../../hooks/useCrossNodeNav';
import './YourNotesNode.css';

/**
 * YourNotesNode
 *
 * Renders the active node's entry list, node switcher and pager.
 * Pure rendering component — no business logic, no props.
 * All state comes from GroupNodeEngine via useYourNotesNode hook.
 *
 * Relevance:
 * - Composes YourNotesNodeHeader, YourNotesNodePager and entry list
 * - Reacts to engine state changes via hook subscription
 * - WASD + Arrow navigation via useKeyboardNav + useCrossNodeNav
 */
export default function YourNotesNode() {
    const { activeNode, nodes, selectedEntry, selectEntry, getCurrentPage, loadPage } = useYourNotesNode();

    // Hide completed toggle — local UI state, no engine involvement
    const [showCompleted, setShowCompleted] = useState(true);
    const [searchOpen, setSearchOpen] = useState(false);

    // Entries and paging come from engine via hook — no local state needed
    const node = nodes?.length > 0 ? nodes.find((d) => d.key === activeNode) : null;
    const allEntries = node?.entries ?? [];
    const completedCount = allEntries.filter((e) => e.stateId === 1).length;
    const entries = showCompleted ? allEntries : allEntries.filter((e) => e.stateId !== 1);
    const total = node?.total ?? 0;
    const currentPage = getCurrentPage(activeNode) ?? 1;
    const totalPages = Math.ceil(total / 25) || 1;

    // Ref for scroll-into-view on selected entry
    const listRef = useRef(null);

    // Scroll selected entry into view when selection changes
    useEffect(() => {
        if (!selectedEntry || !listRef.current) return;
        setTimeout(() => {
            const selected = listRef.current?.querySelector('.fr-card--selected');
            if (!selected) return;
            selected.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }, 50);
    }, [selectedEntry, entries]);

    // WASD + Arrow navigation within current node and page
    // CHANGED — onPageChange passes direction so engine auto-selects first/last entry
    useKeyboardNav({
        entries,
        selectedRk: selectedEntry?.rk,
        onSelect: selectEntry,
        meta: { page: currentPage },
        totalPages,
        onPageChange: (page, direction) => loadPage(activeNode, page, direction),
    });

    // Cross-node navigation — only fires when at top of page 1 or bottom of last page
    useCrossNodeNav({
        activeNode,
        selectedRk: selectedEntry?.rk,
        onSelect: selectEntry,
    });

    // Render empty state when no entries available
    const renderEmpty = () => (
        <Typography variant="caption" sx={{ color: '#a3d1ff', p: 1, display: 'block' }}>
            No entries.
        </Typography>
    );

    // Render entry list with position indicator on selected row
    const renderEntries = () => {
        const selectedIndex = entries.findIndex((e) => e.rk === selectedEntry?.rk);
        return entries.map((e) => (
            <YourNotesRow
                key={e.rk}
                entry={e}
                selected={selectedEntry?.rk === e.rk}
                onClick={selectEntry}
                position={selectedEntry?.rk === e.rk ? selectedIndex + 1 : null}
                total={total}
            />
        ));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* This is dropdown for node selections  */}
            <YourNotesNodeHeader onSearchOpen={() => setSearchOpen(true)} />

            {/* this is the pager row wit checkbox for show completed */}
            <YourNotesNodePager showCompleted={showCompleted} setShowCompleted={setShowCompleted} completedCount={completedCount} />

            <Divider sx={{ borderBottom: '0.5px solid #294766' }} />

            {/* this renders the journal entry headers under dropdown  */}
            <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', pt: 1 }}>
                {entries.length === 0 ? renderEmpty() : renderEntries()}
            </Box>

            <TopicGroupSearchDialog
                open={searchOpen}
                onClose={() => setSearchOpen(false)}
                service={topicGroupEngine.selectedGroup()?.name}
            />
        </Box>
    );
}
